import React, { useEffect, useMemo, useState } from 'react';
import Stepper from 'components/Stepper';
import { useForm } from 'react-hook-form';
import { Checkbox } from 'components/ui-kits/Form/Checkbox';
import { useMutation, useQuery } from 'react-query';
import {
  getMerchantSubscriptionProducts,
  registerMerchantSubscriptionPlan
} from 'services/merchantService';
import Loader from 'components/Loader';
import { formatDollar, handleError } from 'utils';
import { PAYMENT_TOKEN_KEY } from 'const/Const';
import { Dialog } from 'components/Dialog';
import DisplayText from 'components/ui-kits/DisplayText';
import accountingReportScreenshot from 'assets/accountingReportScreenshot.png';
import styled from 'styled-components';
import { COLORS } from 'styles/variables';
import Icon, { IconNames } from 'components/ui-kits/Icon/Icon';
import { useHistory } from 'react-router-dom';

type Props = {
  onClose: (params?: any) => void;
  showOverlay?: boolean;
};

const Image = styled.img`
  width: 100%;
  margin: 1rem 0;
  min-height: 27rem;
`;
const Wrapper = styled.div`
  a {
    font-size: 1.8rem;
    color: ${COLORS.BLACK_2};
    text-decoration: underline;
  }

  .custom-list {
    display: flex;
    align-items: baseline;
    gap: 2rem;
    margin: 2rem 0 2rem 2rem;
  }
`;
const PriceDetail = styled.div<{ margin?: string }>`
  margin: ${({ margin }) => margin};
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const ModalAccountReportSubscription = ({
  onClose,
  showOverlay = false
}: Props) => {
  const [isChangingSite, setIsChangingSite] = useState(false);
  const history = useHistory();
  const { register, watch } = useForm({
    defaultValues: { acceptTerms: false }
  });
  const isAccepted = watch('acceptTerms');

  const {
    data: financialReportPlan,
    isLoading: isSubscriptionFeaturesLoading
  } = useQuery(
    'subscriptionFeatures',
    ({ signal }) => getMerchantSubscriptionProducts(signal),
    {
      staleTime: Infinity
    }
  );

  const onConfrim = () => {
    history.push('/merchant-registration/plans');
  };

  const { isLoading, mutateAsync } = useMutation({
    mutationFn: registerMerchantSubscriptionPlan,
    onError: () => handleError(),
    onSettled: () => setIsChangingSite(false)
  });

  // handle back button bfcache that causes loader to presist the state
  // https://web.dev/articles/bfcache#spa
  useEffect(() => {
    const hideLoader = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsChangingSite(false);
      }
    };

    window.addEventListener('pageshow', hideLoader);

    return () => {
      window.removeEventListener('pageshow', hideLoader);
    };
  }, []);

  const selectedPlan = useMemo(() => {
    const isYearly = localStorage.getItem('isYearly') === 'true';
    const option = financialReportPlan?.find((item) =>
      isYearly ? item.method === 'yearly' : item.method === 'monthly'
    );

    return {
      discount: option?.discount || 0,
      amount: option?.amount || 0,
      vat: option?.vat || 0,
      amountTTC: option?.amountTTC || 0,
      dueDate: option?.dueDate || ''
    };
  }, [financialReportPlan]);

  const hasDiscount = selectedPlan.discount > 0;

  const priceAfterDiscount =
    selectedPlan.amount - selectedPlan.amount * selectedPlan.discount;
  const vatPrice =
    selectedPlan.vat *
    (hasDiscount ? priceAfterDiscount : selectedPlan.amount) *
    100;

  const steps: React.ComponentPropsWithoutRef<typeof Stepper>['steps'] = [
    {
      id: 'about-feature',
      content: (
        <>
          <DisplayText fontSize="L" color="BLACK_6">
            Le rapport des ventes vous permet de consulter et télécharger (PDF)
            votre rapport des ventes par mode de paiement et taux de TVA pour
            une période spécifique ou par mois.
          </DisplayText>
          <DisplayText fontSize="L" color="BLACK_6">
            Rapport idéal à transmettre à votre comptable pour effectuer vos
            obligations déclaratives.
          </DisplayText>
          <Image
            src={accountingReportScreenshot}
            alt="accounting report feature"
          />
          <DisplayText fontSize="L" color="GREY_14">
            Le reporting des ventes débute à partir de la souscription à la
            formule STANDARD et ne couvre pas les périodes antérieures.
          </DisplayText>
          <DisplayText fontSize="L" color="GREY_14">
            En cas de résiliation de la formule STANDARD, l’accès aux rapports
            reste disponible jusqu’à la date de fin de l’abonnement STANDARD,
            puis suspendu.
          </DisplayText>
          <DisplayText fontSize="L" color="GREY_14">
            En cas de réabonnement, seules les données issues des périodes
            d’abonnement précédentes seront à nouveau accessibles.
          </DisplayText>
        </>
      ),
      header: 'Rapport des ventes',
      headerButtonConfig: {
        closeButton: {
          onClick: onClose
        }
      },
      footerConfig: {
        previousButton: {
          onClick: onClose,
          label: 'Annuler'
        },
        nextButton: {
          label: 'Changer de formule',
          onClick: onConfrim
        }
      }
    },
    {
      id: 'accept-conditions-step',
      header: 'Confirmez votre abonnement',
      content: (
        <>
          <Wrapper>
            <PriceDetail>
              <DisplayText fontSize="L" fontWeight="BOLD">
                Rapport des ventes
              </DisplayText>
              <DisplayText fontSize="L" fontWeight="BOLD">
                {formatDollar(selectedPlan.amount * 100)}€ HT / mois
              </DisplayText>
            </PriceDetail>
            <DisplayText fontSize="M" textAlign="end" margin="0.5rem 0 0 0">
              OFFERT en activant le paiement en ligne
            </DisplayText>
            <DisplayText
              fontSize="L"
              fontWeight="BOLD"
              textAlign="center"
              margin="3rem 0">
              Conditions générales de vente Rapport des Ventes
            </DisplayText>
            <DisplayText fontSize="L">
              Nous vous invitons à prendre connaissance des Conditions générales
              de vente du Rapport des Ventes en cliquant sur le lien ci-dessous
              :
            </DisplayText>
            <div className="custom-list">
              <Icon
                name={IconNames.DotIcon}
                width="0.6rem"
                height="0.6rem"
                style={{ scale: '6' }}
              />
              <a
                target="_blank"
                href="https://achil.com/web/cgv-rapportdesventes/"
                rel="noreferrer">
                Consulter les Conditions générales de vente du Rapport des
                Ventes
              </a>
            </div>
          </Wrapper>
          <Checkbox
            fontSize="M"
            register={register}
            name="acceptTerms"
            className="accept-terms"
            id="acceptTerms"
            labelSide="left">
            En cochant cette case, je confirme avoir lu et accepté les
            Conditions générales de vente du Rapport des Ventes.
          </Checkbox>
        </>
      ),
      footerConfig: {
        previousButton: {
          onClick: onClose,
          label: 'Annuler'
        },
        nextButton: {
          disabled: !isAccepted,
          label: 'Changer de formule'
        }
      }
    },
    {
      id: 'confirm-step',
      header: 'Confirmez votre abonnement',
      content: (
        <div>
          <PriceDetail margin="1rem 0 0 0">
            <DisplayText fontSize="L" fontWeight="BOLD">
              Rapport des ventes (HT)
            </DisplayText>
            <DisplayText fontSize="L" fontWeight="BOLD">
              {formatDollar(selectedPlan.amount * 100)}€
            </DisplayText>
          </PriceDetail>
          <DisplayText fontSize="M" textAlign="end" margin="0.5rem 0 2rem 0">
            OFFERT en activant le paiement en ligne
          </DisplayText>
          {selectedPlan.discount > 0 && (
            <PriceDetail margin="1rem 0 2rem 0">
              <DisplayText fontSize="L" fontWeight="BOLD">
                Réduction
              </DisplayText>
              <DisplayText fontSize="L" fontWeight="BOLD">
                {(selectedPlan.discount * 100).toFixed(2)}%
              </DisplayText>
            </PriceDetail>
          )}
          <PriceDetail margin="0 0 1.3rem 0">
            <DisplayText fontSize="L" fontWeight="BOLD">
              TVA ({(selectedPlan.vat * 100).toFixed(2)}%)
            </DisplayText>
            <DisplayText fontSize="L" fontWeight="BOLD">
              {formatDollar(vatPrice)}€
            </DisplayText>
          </PriceDetail>
          <div className="divider" />
          <PriceDetail margin="1.3rem 0 2rem">
            <DisplayText fontSize="L" fontWeight="BOLD">
              Total (TTC)
            </DisplayText>
            <DisplayText fontSize="L" fontWeight="BOLD">
              {formatDollar(selectedPlan.amountTTC * 100)}€
            </DisplayText>
          </PriceDetail>
          <DisplayText fontSize="L">
            Ce montant sera débité chaque mois le{' '}
            <strong>{selectedPlan.dueDate}</strong> sur votre carte bancaire.
          </DisplayText>
        </div>
      ),
      footerConfig: {
        previousButton: {
          label: 'Annuler'
        },
        nextButton: {
          label: 'Abonnez-vous',
          onClick: () => {
            mutateAsync(window.origin).then((data) => {
              if (
                data.data &&
                data.data.redirect_url &&
                data.data.payment_token
              ) {
                localStorage.setItem(
                  PAYMENT_TOKEN_KEY,
                  data.data.payment_token
                );

                setIsChangingSite(true);
                window.location.href = data.data.redirect_url;
              }
            });
          }
        }
      }
    }
  ];

  return (
    <>
      {(isLoading || isChangingSite || isSubscriptionFeaturesLoading) && (
        <Loader />
      )}
      <Dialog
        minWidth="55rem"
        maxWidth="55rem"
        borderRadius="0.7rem"
        showOverlay={showOverlay}>
        <Stepper steps={steps} />
      </Dialog>
    </>
  );
};

export default ModalAccountReportSubscription;
